// Server automations on the web (specs/web-automations WAU-01, -02, -10, -12, -13; vision-api 0.55.0): the list, one
// automation with its document, the run log and the device page's section, worded as the phones word them
// (creavision-app-ios specs/automations IOS-AUT-02, -05 to -07).
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { apiClient, unwrap } from "../../api/client";
import type { components } from "../../api/generated/vision-api";
import { ApiError } from "../../api/problem";
import { formatNumber } from "../../i18n/format";
import { allPages, type Device } from "../devices/queries";

export type AutomationItem = components["schemas"]["AutomationItem"];
export type Automation = components["schemas"]["Automation"];
export type AutomationRun = components["schemas"]["AutomationRun"];
export type Paused = NonNullable<AutomationItem["paused_reason"]>;
type Point = components["schemas"]["PointInfo"];
type ActionInfo = components["schemas"]["ActionInfo"];

const PAGE = 500;
/** One page of the run log. */
export const RUNS_PAGE = 50;

// ---------------------------------------------------------------------------------------------------------------
// Queries. Keys follow the contract's routes (AGENTS.md); "all" is every page of the list.

export const automationsKey = (orgId: string) => ["org", orgId, "/v1/automations", "all"] as const;

/** Every server automation the person may see, all pages, by name (BE-AUT-15). */
export const automationsQuery = (orgId: string) =>
  queryOptions({
    queryKey: automationsKey(orgId),
    queryFn: () =>
      allPages((cursor) =>
        unwrap(apiClient("customers").GET("/v1/automations", { params: { query: { limit: PAGE, cursor } } })),
      ),
    enabled: !!orgId,
    refetchOnWindowFocus: true,
  });

export const automationKey = (orgId: string, id: string) => ["org", orgId, "/v1/automations/{automation_id}", id];

/** One automation with its document (getAutomation). */
export const automationQuery = (orgId: string, id: string) =>
  queryOptions({
    queryKey: automationKey(orgId, id),
    queryFn: () =>
      unwrap(
        apiClient("customers").GET("/v1/automations/{automation_id}", { params: { path: { automation_id: id } } }),
      ),
    enabled: !!orgId,
  });

/** The run log, newest first, a page at a time (WAU-13, kept 90 days). */
export const runsQuery = (orgId: string, id: string) =>
  infiniteQueryOptions({
    queryKey: ["org", orgId, "/v1/automations/{automation_id}/runs", id],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        apiClient("customers").GET("/v1/automations/{automation_id}/runs", {
          params: { path: { automation_id: id }, query: { limit: RUNS_PAGE, cursor: pageParam } },
        }),
      ),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!orgId,
  });

export const deviceAutomationsKey = (orgId: string, deviceId: string) => [
  "org",
  orgId,
  "/v1/devices/{device_id}/automations",
  deviceId,
];

/** The automations that name one device (WAU-01, listDeviceAutomations), all pages. */
export const deviceAutomationsQuery = (orgId: string, deviceId: string, enabled = true) =>
  queryOptions({
    queryKey: deviceAutomationsKey(orgId, deviceId),
    queryFn: () =>
      allPages((cursor) =>
        unwrap(
          apiClient("customers").GET("/v1/devices/{device_id}/automations", {
            params: { path: { device_id: deviceId }, query: { limit: PAGE, cursor } },
          }),
        ),
      ),
    enabled: enabled && !!orgId,
  });

/** Every device the person can view, once, for the names and the locked state (not one request per device). */
export const automationDevicesQuery = (orgId: string) =>
  queryOptions({
    // Not under "/v1/devices": the live patcher treats every query there as a paged device list.
    queryKey: ["org", orgId, "automation-devices"],
    queryFn: async (): Promise<Map<string, Device>> => {
      const items = await allPages((cursor) =>
        unwrap(apiClient("customers").GET("/v1/devices", { params: { query: { limit: PAGE, cursor } } })),
      );
      return new Map(items.map((d) => [d.id, d]));
    },
    enabled: !!orgId,
    staleTime: 60_000,
  });

export async function setEnabled(id: string, enabled: boolean): Promise<AutomationItem> {
  return unwrap(
    apiClient("customers").PATCH("/v1/automations/{automation_id}", {
      params: { path: { automation_id: id } },
      body: { enabled },
    }),
  );
}

export async function deleteAutomation(id: string): Promise<void> {
  await unwrap(
    apiClient("customers").DELETE("/v1/automations/{automation_id}", { params: { path: { automation_id: id } } }),
  );
}

export async function runAutomation(id: string): Promise<void> {
  await unwrap(
    apiClient("customers").POST("/v1/automations/{automation_id}/run", { params: { path: { automation_id: id } } }),
  );
}

/** A list item with what changed, in every list that holds it. */
export function replaceItem(items: AutomationItem[] | undefined, next: Partial<AutomationItem> & { id: string }) {
  return items?.map((a) => (a.id === next.id ? { ...a, ...next } : a));
}

// ---------------------------------------------------------------------------------------------------------------
// Status, filters and the locked state.

export type Status = { kind: "off" } | { kind: "paused"; reason: Paused } | { kind: "failed" } | { kind: "fine" };

/** What the status line says, first match first (IOS-AUT-02). */
export function statusOf(a: Pick<AutomationItem, "enabled" | "paused_reason" | "last_result">): Status {
  if (!a.enabled) return { kind: "off" };
  if (a.paused_reason) return { kind: "paused", reason: a.paused_reason };
  return a.last_result === "failed" ? { kind: "failed" } : { kind: "fine" };
}

/** The status line needs attention. */
export const warns = (s: Status) => s.kind === "paused" || s.kind === "failed";

/** Run is offered: a scene that is on and not paused (otherwise the server answers 409). */
export const canRun = (a: Pick<AutomationItem, "manual" | "enabled" | "paused_reason">) =>
  !!a.manual && a.enabled && !a.paused_reason;

export const PAUSE_KEYS: Record<Paused, string> = {
  plan_limit: "automations.paused.planLimit",
  owner_lost_access: "automations.paused.ownerLostAccess",
  loop: "automations.paused.loop",
  device_gone: "automations.paused.deviceGone",
};

export const STATES = ["on", "off", "attention"] as const;
export type StateFilter = (typeof STATES)[number];
export type AutomationFilters = { device?: string; state?: StateFilter };

export function parseAutomationFilters(search: Record<string, unknown>): AutomationFilters {
  const out: AutomationFilters = {};
  if (typeof search.device === "string" && search.device) out.device = search.device;
  if (typeof search.state === "string" && (STATES as readonly string[]).includes(search.state))
    out.state = search.state as StateFilter;
  return out;
}

/** WAU-10's filters: by a device it names, and by on, off or needing attention (paused or failed). */
export function filterAutomations(items: readonly AutomationItem[], f: AutomationFilters): AutomationItem[] {
  return items.filter((a) => {
    if (f.device && !(a.device_ids ?? []).includes(f.device)) return false;
    if (f.state === "on" && !a.enabled) return false;
    if (f.state === "off" && a.enabled) return false;
    if (f.state === "attention" && !warns(statusOf(a))) return false;
    return true;
  });
}

/** The person may act on the device in an automation: control and automate (AUT-V-03). */
export const canAutomate = (d: Pick<Device, "permissions">) =>
  d.permissions.includes("control") && d.permissions.includes("automate");

/** The device's plan includes server automations: a limit of 0 means not included (PL-30). */
export const planAllows = (d: Pick<Device, "plan_limits">) => d.plan_limits?.automations !== 0;

/**
 * Locked (WAU-02, IOS-AUT-05): no device the person can act on has a plan with server automations (Free and Basic),
 * so the page says they come with Plus and above.
 */
export const locked = (devices: Iterable<Device>) => {
  for (const d of devices) if (canAutomate(d) && planAllows(d)) return false;
  return true;
};

/** The device whose plans "See plans" opens: one the person can act on, else any. */
export function planDevice(devices: Iterable<Device>): Device | undefined {
  let first: Device | undefined;
  for (const d of devices) {
    if (canAutomate(d)) return d;
    first ??= d;
  }
  return first;
}

// ---------------------------------------------------------------------------------------------------------------
// The document read as sentences (WAU-12, IOS-AUT-06). Only the R1 blocks have words; any other block says it is
// kept as it is.

type T = (key: string, options?: Record<string, unknown>) => string;
type Block = Record<string, unknown>;

/** Names of what a document points at, from the device list and each device's page document. */
export type Names = {
  device: (id: string) => string;
  point: (device: string, key: string) => Point | undefined;
  action: (device: string, key: string) => ActionInfo | undefined;
  alarm: (device: string, key: string) => string | undefined;
};

export type Blocks = { triggers: Block[]; conditions: Block[]; actions: Block[] };

const blocks = (v: unknown): Block[] =>
  Array.isArray(v) ? v.filter((b): b is Block => typeof b === "object" && b !== null) : [];

/** The three lists of a server document; empty lists for anything else. */
export function documentBlocks(doc: Record<string, unknown> | undefined): Blocks {
  return { triggers: blocks(doc?.triggers), conditions: blocks(doc?.conditions), actions: blocks(doc?.actions) };
}

const str = (v: unknown) => (typeof v === "string" ? v : undefined);

/** The one device a block names: `device`, or `targets.device` (groups and spaces are not R1). */
export function blockDevice(b: Block): string | undefined {
  const direct = str(b.device);
  if (direct) return direct;
  const targets = b.targets;
  if (typeof targets === "object" && targets !== null) return str((targets as Block).device);
  return undefined;
}

/** Every device id a document names, in order, once each. */
export function documentDevices(doc: Record<string, unknown> | undefined): string[] {
  const { triggers, conditions, actions } = documentBlocks(doc);
  const ids = [...triggers, ...conditions, ...actions].map(blockDevice).filter((d): d is string => !!d);
  return [...new Set(ids)];
}

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

/** "Mon, Wed and Fri", Monday first, in the language's words. */
export function daysText(days: unknown, lang: string): string {
  const picked = Array.isArray(days) ? DAYS.filter((d) => days.includes(d)) : [];
  // 2024-01-01 was a Monday.
  const names = picked.map((d) =>
    new Intl.DateTimeFormat(lang, { weekday: "short", timeZone: "UTC" }).format(
      new Date(Date.UTC(2024, 0, 1 + DAYS.indexOf(d))),
    ),
  );
  return new Intl.ListFormat(lang, { style: "long", type: "conjunction" }).format(names);
}

/** "for 30 min", "for 2 h"; empty without a wait. */
export function waitText(duration: unknown, t: T): string {
  const m = typeof duration === "string" ? /^(\d+)(s|m|h|d)$/.exec(duration) : null;
  if (!m) return "";
  const n = Number(m[1]);
  if (n <= 0) return "";
  const unit = m[2];
  if (unit === "s") return t("automations.forSeconds", { count: n });
  if (unit === "d") return t("automations.forHours", { count: n * 24 });
  if (unit === "h") return t("automations.forHours", { count: n });
  return n % 60 === 0 ? t("automations.forHours", { count: n / 60 }) : t("automations.forMinutes", { count: n });
}

/** A value in its unit, an enum's label, or on/off. */
export function valueText(value: unknown, t: T, lang: string, unit?: string | null, labels?: Record<string, string>) {
  if (typeof value === "number") {
    const text = formatNumber(value, lang, { maximumFractionDigits: 3 });
    return unit ? `${text} ${unit}` : text;
  }
  if (typeof value === "boolean") return t(value ? "automations.value.on" : "automations.value.off");
  if (typeof value === "string") return labels?.[value] ?? value;
  return "";
}

const withWait = (text: string, wait: string) => (wait ? `${text} ${wait}` : text);

export function triggerText(b: Block, names: Names, t: T, lang: string): string {
  const device = blockDevice(b);
  switch (b.type) {
    case "datapoint": {
      if (!device || !str(b.datapoint)) break;
      const point = names.point(device, b.datapoint as string);
      const op = "above" in b ? "above" : "below" in b ? "below" : "equals" in b ? "equals" : undefined;
      if (!op) break;
      return withWait(
        t(`automations.when.${op}`, {
          point: point?.name ?? b.datapoint,
          device: names.device(device),
          value: valueText(b[op], t, lang, point?.unit, point?.labels),
        }),
        waitText(b.for, t),
      );
    }
    case "schedule": {
      const at = str(b.at);
      if (!at) break;
      const days = Array.isArray(b.weekdays) && b.weekdays.length > 0 && b.weekdays.length < 7;
      const text = days
        ? t("automations.when.onDays", { at, days: daysText(b.weekdays, lang) })
        : t("automations.when.everyDay", { at });
      const tz = str(b.tz);
      return tz && tz !== DEFAULT_ZONE ? `${text} (${tz})` : text;
    }
    case "device_status": {
      if (!device) break;
      const key = b.status === "online" ? "automations.when.online" : "automations.when.offline";
      return withWait(t(key, { device: names.device(device) }), waitText(b.for, t));
    }
    case "alarm": {
      const alarm = str(b.alarm);
      if (!device || !alarm) break;
      const key = b.state === "cleared" ? "automations.when.alarmCleared" : "automations.when.alarmRaised";
      return t(key, { alarm: names.alarm(device, alarm) ?? alarm, device: names.device(device) });
    }
    case "manual":
      return t("automations.when.manual");
  }
  return t("automations.block.other");
}

export function conditionText(b: Block, names: Names, t: T, lang: string): string {
  const device = blockDevice(b);
  switch (b.type) {
    case "datapoint": {
      if (!device || !str(b.datapoint)) break;
      const point = names.point(device, b.datapoint as string);
      const op = (["above", "below", "equals", "not_equals"] as const).find((k) => k in b);
      if (!op) break;
      return t(`automations.if.${op === "not_equals" ? "notEquals" : op}`, {
        point: point?.name ?? b.datapoint,
        device: names.device(device),
        value: valueText(b[op], t, lang, point?.unit, point?.labels),
      });
    }
    case "time_window":
      if (!str(b.from) || !str(b.to)) break;
      return t("automations.if.between", { from: b.from, to: b.to });
    case "weekday":
      return t("automations.if.onDays", { days: daysText(b.days, lang) });
    case "device_online":
      if (!device) break;
      return t(b.online === false ? "automations.if.offline" : "automations.if.online", {
        device: names.device(device),
      });
  }
  return t("automations.block.other");
}

export function actionText(b: Block, names: Names, t: T, lang: string): string {
  const device = blockDevice(b);
  switch (b.type) {
    case "command": {
      const command = str(b.command);
      if (!device || !command) break;
      const info = names.action(device, command);
      const text = t("automations.then.command", { command: info?.name ?? command, device: names.device(device) });
      const params = typeof b.params === "object" && b.params !== null ? Object.entries(b.params as Block) : [];
      if (params.length === 0) return text;
      const parts = params
        .sort(([a], [z]) => a.localeCompare(z))
        .map(([k, v]) => `${k} ${valueText(v, t, lang)}`)
        .join(", ");
      return `${text} (${parts})`;
    }
    case "setting": {
      const setting = str(b.setting);
      if (!device || !setting) break;
      const info = names.action(device, setting);
      return t("automations.then.setting", {
        setting: info?.name ?? setting,
        device: names.device(device),
        value: valueText(b.value, t, lang, info?.unit),
      });
    }
    case "notify": {
      // R1 sends e-mail to the person who saved it ("E-mail me", BE-AUT-04).
      const message = str(b.message);
      if (!message) break;
      return t("automations.then.email", { message });
    }
    case "alarm": {
      const alarm = str(b.alarm);
      if (!alarm) break;
      const severity = str(b.severity) ?? "warning";
      const text = t("automations.then.alarm", {
        alarm: str(b.message) ?? alarm,
        severity: t(`widget:severity.${severity}`),
      });
      return device ? `${text} · ${names.device(device)}` : text;
    }
  }
  return t("automations.block.other");
}

/** The time zone the phones and the builder use unless the person picks another (IOS-AUT-09). */
export const DEFAULT_ZONE = "Europe/Bucharest";

// ---------------------------------------------------------------------------------------------------------------
// The run log.

export const RUN_RESULT_KEYS: Record<AutomationRun["result"], string> = {
  running: "automations.result.running",
  ok: "automations.result.ok",
  failed: "automations.result.failed",
  skipped: "automations.result.skipped",
};

export const ACTION_RESULT_KEYS: Record<AutomationRun["actions"][number]["result"], string> = {
  queued: "automations.action.queued",
  executed: "automations.action.executed",
  failed: "automations.action.failed",
  refused: "automations.action.refused",
  sent: "automations.action.sent",
};

/** What fired a run, as a sentence: a person's Run, or the document's trigger with the value it read. */
export function firedText(run: AutomationRun, names: Names, t: T, lang: string): string {
  const trigger = run.trigger as Block;
  if (trigger.type === "manual") return t("automations.runs.byHand");
  const text = triggerText(trigger, names, t, lang);
  const device = blockDevice(trigger);
  if (trigger.type === "datapoint" && "value" in trigger && device) {
    const point = str(trigger.datapoint) ? names.point(device, trigger.datapoint as string) : undefined;
    return `${text} · ${t("automations.runs.read", { value: valueText(trigger.value, t, lang, point?.unit, point?.labels) })}`;
  }
  return text;
}

/** A condition's line in a run: its sentence, met or not, and the value it read. */
export function checkText(
  check: AutomationRun["conditions"][number],
  conditions: Block[],
  names: Names,
  t: T,
  lang: string,
): string {
  const block = conditions[check.index];
  const text = block ? conditionText(block, names, t, lang) : t("automations.block.other");
  const met = t(check.passed ? "automations.runs.passed" : "automations.runs.notPassed");
  if (check.value === undefined || check.value === null) return `${text}: ${met}`;
  const device = block ? blockDevice(block) : undefined;
  const point = device && block && str(block.datapoint) ? names.point(device, block.datapoint as string) : undefined;
  return `${text}: ${met} (${t("automations.runs.read", { value: valueText(check.value, t, lang, point?.unit, point?.labels) })})`;
}

// ---------------------------------------------------------------------------------------------------------------
// Refusals of a switch, Run or delete, in words (IOS-AUT-03, -13; WAU-25 adds the builder's).

export function refusedText(e: unknown, names: Pick<Names, "device">, t: T): string {
  if (!(e instanceof ApiError)) return t("automations.error.failed");
  const device = e.problem?.device_id;
  switch (e.status) {
    case 400:
      return e.problem?.field
        ? t("automations.error.invalidField", { field: e.problem.field })
        : t("automations.error.invalid");
    case 403:
      return device
        ? t("automations.error.forbiddenDevice", { device: names.device(device) })
        : t("automations.error.forbidden");
    case 404:
      return t("automations.error.notFound");
    case 409:
      if (e.code === "plan_limit")
        return device
          ? t("automations.error.planLimitDevice", { device: names.device(device) })
          : t("automations.error.planLimit");
      if (e.code === "automation_changed") return t("automations.error.changed");
      return e.problem?.detail
        ? t("automations.error.cannotRun", { reason: e.problem.detail })
        : t("automations.error.cannotRunPlain");
    case 422:
      return t("automations.error.notYet");
    case 501:
      return t("automations.notYet");
  }
  return t("automations.error.failed");
}

/** A refusal that "See plans" answers. */
export const isPlanLimit = (e: unknown) => e instanceof ApiError && e.status === 409 && e.code === "plan_limit";
